import React, { useEffect, useMemo, useState } from "react";
import { TextField } from "components/ui/TextField";
import { Button } from "components/ui/Button";
import { Modal } from "components/ui/Modal";
import { getCategories, createCategory, updateItem } from "api/items";
import { listSuppliers } from "api/suppliers";

export const EditItemModal = ({ open, onClose, item, onUpdated }) => {
  // === ДОБАВЛЕНО СОСТОЯНИЕ ДЛЯ ЦЕНЫ ===
  const [price, setPrice] = useState("");
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("шт");
  const [minStock, setMinStock] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [cats, setCats] = useState([]);

  const [newCat, setNewCat] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [isConsumable, setIsConsumable] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [suppliers, setSuppliers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setError(null);
        const data = await getCategories();
        setCats(Array.isArray(data) ? data : []);
        const sups = await listSuppliers();
        setSuppliers(Array.isArray(sups) ? sups : []);
      } catch (e) {
        setError(e.message || "Ma'lumotlarni olib bo'lmadi");
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !item) return;
    setSku(item.sku || "");
    setName(item.name || "");
    setUnit(item.unit || "шт");
    // === ДОБАВЛЕНО ЗАПОЛНЕНИЕ ЦЕНЫ ===
    setPrice(item.price === 0 || item.price ? String(item.price) : "");
    setMinStock(
      item.min_stock === 0 || item.min_stock ? String(item.min_stock) : ""
    );
    // Убеждаемся, что category_id преобразуется в строку
    setCategoryId(item.category_id ? String(item.category_id) : "");
    setIsConsumable(item.is_consumable || false);
    setSupplier(item.supplier_id ? String(item.supplier_id) : "");
    setError(null);
    setTouched({});
  }, [open, item]);

  // === ДОБАВЛЕНЫ NUM-ВЕРСИИ ДЛЯ ВАЛИДАЦИИ ===
  const minStockNum = useMemo(
    () => (minStock === "" ? "" : Number(minStock)),
    [minStock]
  );
  const priceNum = useMemo(() => (price === "" ? "" : Number(price)), [price]);

  const canSubmit = useMemo(() => {
    if (!item) return false;
    if (!name.trim()) return false;
    if (minStock !== "" && (Number.isNaN(minStockNum) || minStockNum < 0))
      return false;
    // === ДОБАВЛЕНА ВАЛИДАЦИЯ ЦЕНЫ ===
    if (price !== "" && (Number.isNaN(priceNum) || priceNum < 0)) return false;
    return true;
  }, [item, name, minStock, minStockNum, price, priceNum]);

  const fieldError = (key) => {
    if (!touched[key]) return null;
    if (key === "name" && !name.trim()) return "Nomini kiritish shart";
    if (
      key === "minStock" &&
      minStock !== "" &&
      (Number.isNaN(minStockNum) || minStockNum < 0)
    )
      return "Min. qoldiq ≥ 0 bo'lishi kerak";
    // === ДОБАВЛЕНА ПРОВЕРКА ОШИБКИ ЦЕНЫ ===
    if (
      key === "price" &&
      price !== "" &&
      (Number.isNaN(priceNum) || priceNum < 0)
    )
      return "Narx ≥ 0 bo'lishi kerak";
    return null;
  };

  const resetLocal = () => {
    setSku("");
    setName("");
    setUnit("шт");
    setMinStock("");
    setPrice(""); // <-- сброс
    setCategoryId("");
    setNewCat("");
    setIsConsumable(false);
    setError(null);
    setTouched({});
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
    resetLocal();
  };

  const addCategory = async () => {
    const title = newCat.trim();
    if (!title || addingCat) return;
    setAddingCat(true);
    try {
      const created = await createCategory({ name: title }); // Передаем объект
      if (created?.id) {
        setCats((prev) =>
          prev.some((c) => c.id === created.id) ? prev : [...prev, created]
        );
        setCategoryId(String(created.id));
        setNewCat("");
      }
    } catch (e) {
      // ... (логика обработки ошибок осталась без изменений)
    } finally {
      setAddingCat(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        handleClose();
      }
      if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        submit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, name, minStock, price, isConsumable, canSubmit, loading]); // <-- добавлена цена и isConsumable в зависимости

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched({ name: true, minStock: true, price: true }); // <-- добавлено
    if (!item || !canSubmit) return;

    setLoading(true);
    setError(null);
    try {
      // Преобразуем categoryId в число или null
      const finalCategoryId = categoryId && categoryId !== "" ? Number(categoryId) : null;
      // Преобразуем supplier в число или null
      const finalSupplierId = supplier && supplier !== "" ? Number(supplier) : null;

      const payload = {
        sku: sku.trim() || null,
        name: name.trim(),
        category_id: finalCategoryId,
        unit: (unit || "").trim() || "шт",
        min_stock: minStock === "" ? 0 : Number(minStockNum),
        // === ДОБАВЛЕНО ПОЛЕ ЦЕНЫ В PAYLOAD ===
        price: price === "" ? 0 : Number(priceNum),
        supplier_id: finalSupplierId,
        is_consumable: isConsumable,
      };

      const updated = await updateItem(item.id, payload);

      // Передаем обновленный товар в onUpdated
      onUpdated?.(updated);
      handleClose();
    } catch (e) {
      setError(e.message || "Saqlashda xatolik yuz berdi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Mahsulotni tahrirlash"
      className="bg-gray-50 dark:bg-slate-900 text-gray-800 dark:text-slate-100"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {canSubmit
              ? "Saqlash uchun Ctrl/⌘+Enter"
              : "Majburiy maydonlarni to'ldiring"}
          </div>
          <div className="flex gap-4">
            <Button variant="ghost" onClick={handleClose} disabled={loading} className="text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-800">
              Bekor qilish
            </Button>
            <Button
              onClick={submit}
              disabled={loading || !canSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Saqlanmoqda..." : "Saqlash"}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextField
            label="Artikul (SKU)"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="NB-001"
            className="text-gray-800 dark:text-slate-100"
            inputClassName="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100"
          />
          <TextField
            label="Nomi"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            required
            placeholder="Masalan, Lenovo noutbuki"
            hint={fieldError("name") || "Majburiy maydon"}
            invalid={!!fieldError("name")}
            className="text-gray-800 dark:text-slate-100"
            inputClassName={`bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100 ${fieldError("name") ? "border-red-500" : ""
              }`}
            hintClassName={
              fieldError("name") ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-slate-400"
            }
          />
        </div>

        {/* === ДОБАВЛЕН БЛОК С ЦЕНОЙ И ДРУГИМИ ПОЛЯМИ === */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <TextField
            label="O'lchov birligi"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="шт"
            hint="dona, qadoq, m, kg"
            className="text-gray-800 dark:text-slate-100"
            inputClassName="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100"
          />
          <TextField
            type="number"
            inputMode="decimal"
            label="Birlik narxi"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, price: true }))}
            hint={fieldError("price") || "Sotish/hisobga olish narxi"}
            invalid={!!fieldError("price")}
            className="text-gray-800 dark:text-slate-100"
            inputClassName={`bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100 ${fieldError("price") ? "border-red-500" : ""
              }`}
            hintClassName={
              fieldError("price") ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-slate-400"
            }
          />
          <TextField
            type="number"
            inputMode="decimal"
            label="Min. qoldiq"
            value={minStock}
            onChange={(e) => setMinStock(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, minStock: true }))}
            hint={fieldError("minStock") || "0 — nazoratsiz"}
            invalid={!!fieldError("minStock")}
            className="text-gray-800 dark:text-slate-100"
            inputClassName={`bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-slate-100 ${fieldError("minStock") ? "border-red-500" : ""
              }`}
            hintClassName={
              fieldError("minStock") ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-slate-400"
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            Kategoriya
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <select
              className="w-full rounded-md border-gray-300 dark:border-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">— Kategoriyasiz —</option>
              {cats.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <input
                placeholder="Yoki yangisini yarating"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                className="flex-1 rounded-md border-gray-300 dark:border-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-4 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addCategory}
                disabled={!newCat.trim() || addingCat}
                className="shrink-0 text-gray-700 dark:text-slate-200 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                {addingCat ? "..." : "Qo'shish"}
              </Button>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
          <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
            <input
              type="checkbox"
              checked={isConsumable}
              onChange={(e) => setIsConsumable(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
            />
            <div>
              <span className="block font-medium text-gray-900 dark:text-slate-100">
                Sarf material
              </span>
              <span className="block text-xs text-gray-500 dark:text-slate-400">
                Qaytarish vaqtida ogohlantirish ko'rsatiladi
              </span>
            </div>
          </label>
        </div>

        {/* Supplier */}
        <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            Yetkazib beruvchi
          </label>
          <select
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100"
          >
            <option value="">— Ko'rsatilmagan —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="rounded-md border border-red-300 bg-red-100 dark:bg-red-900/30 px-4 py-3 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
};
